import { useCallback, useRef, useState } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  ArrowsPointingInIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ApiError } from './api';
import { useI18n } from '../i18n';
import { interpolate } from './format';
import { enqueueCompress } from '../organize/compress-api';

/**
 * Modale de COMPRESSION SANS PERTE de la sélection.
 *
 * Ré-optimise les IMAGES JPEG/PNG sans aucune perte de qualité (pixels identiques) ; les
 * vidéos et formats non gérés sont ignorés ; le fichier n'est remplacé que s'il devient
 * plus petit. Deux options : conserver une sauvegarde `_original` (défaut décoché — c'est
 * sans perte, l'utilisateur veut gagner de la place) et simuler d'abord (dry-run, aperçu).
 *
 * Boîte de dialogue accessible : `role="dialog"` + `aria-modal`, focus initial/restauré,
 * verrou de défilement, Échap ferme. Le caller l'enveloppe dans un `<Portal>`.
 */
interface CompressModalProps {
  libraryId: string;
  /** Ids des assets sélectionnés (capturés à l'ouverture). */
  assetIds: string[];
  onClose: () => void;
  /** Appelé après un enfilement réussi (p.ex. vider la sélection + rafraîchir). */
  onQueued?: () => void;
}

export function CompressModal({
  libraryId,
  assetIds,
  onClose,
  onQueued,
}: CompressModalProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const confirmRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const [keepOriginal, setKeepOriginal] = useState(false);
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  useModalA11y({ dialogRef, initialFocusRef: confirmRef, onEscape: !busy ? onClose : undefined });
  const [error, setError] = useState<'writeDisabled' | 'generic' | null>(null);
  const [done, setDone] = useState(false);

  // Verrou de défilement + focus initial + restauration.

  // Échap ferme (sauf pendant l'appel réseau).

  const onSubmit = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await enqueueCompress(libraryId, { assetIds, keepOriginal, dryRun });
      setDone(true);
      onQueued?.();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setError('writeDisabled');
      } else {
        setError('generic');
      }
    } finally {
      setBusy(false);
    }
  }, [busy, libraryId, assetIds, keepOriginal, dryRun, onQueued]);

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('compress.title')}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        {/* En-tête. */}
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <ArrowsPointingInIcon className="h-6 w-6 text-brand" aria-hidden="true" />
            {t('compress.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            title={t('compress.close')}
            aria-label={t('compress.close')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Corps. */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          {done ? (
            <p
              role="status"
              className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-fg"
            >
              <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
              {t('compress.queued')}
            </p>
          ) : (
            <>
              <p className="text-sm text-muted">
                {interpolate(t('compress.intro'), { count: assetIds.length }, locale)}
              </p>
              <p className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-muted">
                <InformationCircleIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-brand"
                  aria-hidden="true"
                />
                {t('compress.losslessNote')}
              </p>

              <label className="flex items-start gap-2 text-sm text-fg">
                <input
                  type="checkbox"
                  checked={keepOriginal}
                  onChange={(e) => setKeepOriginal(e.target.checked)}
                  disabled={busy}
                  className="mt-0.5 h-4 w-4"
                />
                <span>{t('compress.keepOriginal')}</span>
              </label>
              <label className="flex items-start gap-2 text-sm text-fg">
                <input
                  type="checkbox"
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  disabled={busy}
                  className="mt-0.5 h-4 w-4"
                />
                <span>{t('compress.dryRun')}</span>
              </label>

              {error !== null && (
                <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {error === 'writeDisabled'
                    ? t('compress.errorWriteDisabled')
                    : t('compress.error')}
                </p>
              )}
            </>
          )}
        </div>

        {/* Actions. */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          {done ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t('compress.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              >
                {t('compress.cancel')}
              </button>
              <button
                type="button"
                ref={confirmRef}
                onClick={() => void onSubmit()}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy && <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />}
                {busy ? t('compress.submitting') : t('compress.confirm')}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
